// Function to detect top-level domain based on location.origin
const getDomainTLD = () => {
  const domain = location.origin.includes('.ca') ? 'dietid.ca' : 'dietid.com';
  return domain;
};
export const getSubdomain = () => {
  const host = window.location.host; // Get the full host (e.g., subdomain.domain.com or subdomain.localhost)
  const domainParts = host.split('.'); // Split the host into parts

  // Check if it's localhost with a subdomain (e.g., subdomain.localhost)
  if (host.includes('localhost') && domainParts.length === 2) {
    return domainParts[0]; // The subdomain is the first part before 'localhost'
  }

  // Check if there are at least 3 parts (e.g., subdomain.domain.tld)
  if (domainParts.length > 2) {
    return domainParts[0]; // Return the first part as the subdomain
  }

  // No subdomain detected
  return '';
};

// Environment configuration
export const ENVIRONMENT = [
  ['production', location.origin.includes('capacitor://localhost')],
  ['local', !location.origin.includes('capacitor://localhost') && !location.origin.includes(getDomainTLD())],
  ['development', location.origin.includes(`beta.dev.${getDomainTLD()}`)],
  ['staging', location.origin.includes(`beta.staging.${getDomainTLD()}`)],
  [
    'production',
    !location.origin.includes(`beta.staging.${getDomainTLD()}`) && location.origin.includes(getDomainTLD()),
  ],
].find(([name, isActive]) => isActive)[0];

// Get host for Beta client based on environment and partner token
export const getBetaClientHost = (partnerToken) => {
  const domainTLD = getDomainTLD();
  const subdomain = partnerToken || getSubdomain();
  return {
    local: `http://${subdomain}.localhost:3011`,
    development: `https://${subdomain}.beta.dev.${domainTLD}`,
    staging: `https://${subdomain}.beta.staging.${domainTLD}`,
    production: `https://${subdomain}.beta.${domainTLD}`,
  }[ENVIRONMENT];
};

// Get client host based on environment and partner token
// Get client host based on environment and partner token
export const getClientHost = (partnerToken) => {
  const domainTLD = getDomainTLD();
  const subdomain = partnerToken || getSubdomain();
  return {
    local: `http://${subdomain}.localhost:19006`,
    development: `https://${subdomain}.dev.${domainTLD}`,
    staging: `https://${subdomain}.staging.${domainTLD}`,
    production: `https://${subdomain}.${domainTLD}`,
  }[ENVIRONMENT];
};

// API host configuration based on environment
export const API_HOST = {
  local: 'http://localhost:5001/api/v3',
  development: `https://api-dev.${getDomainTLD()}/api/v3`,
  staging: `https://api-staging.${getDomainTLD()}/api/v3`,
  production: `https://api-prod.${getDomainTLD()}/api/v3`,
}[ENVIRONMENT];

// API host configuration for v1
export const API_HOST_V1 = {
  local: 'http://localhost:5001/api/v1',
  development: `https://api-dev.${getDomainTLD()}/api/v1`,
  staging: `https://api-staging.${getDomainTLD()}/api/v1`,
  production: `https://api-prod.${getDomainTLD()}/api/v1`,
}[ENVIRONMENT];

// Google Analytics tracking ID configuration based on environment
export const GOOGLE_ANALYTICS_TRACKING_ID = {
  local: null,
  development: null,
  staging: 'UA-137832774-2',
  production: 'UA-137832774-3',
}[ENVIRONMENT];

const asAdmin = location.search.includes('role=admin');

export const LOCAL_STORAGE_AUTH_KEY = asAdmin ? 'DQPN_ADMIN_AUTH' : 'DQPN_AUTH';
export const LOCAL_STORAGE_PARTNER_TOKEN_KEY = asAdmin ? 'DQPN_ADMIN_PARTNER_TOKEN' : 'DQPN_PARTNER_TOKEN';
export const LOCAL_STORAGE_VIEW_KEY = 'DIET_ID_VIEW';
export const LOCAL_STORAGE_LOCALE_KEY = 'DIET_ID_LOCALE';

export const CONTENTFUL = {
  SPACE_ID: 'hgv8v1edy1w1',
  ACCESS_TOKEN: 'jETAQcqR6i7RHjQ_vhSc877PAnUjLZ29KbyzxlxfBoM',
  ENVIRONMENT: 'master',
};

export const LAUNCH_DARKLY_CLIENT_ID = {
  local: '65d994e63dfa060f68b90161',
  development: '65d994e63dfa060f68b90161',
  staging: '65d9950bf7078f1095e38a78',
  production: '65bbb107dceb5d102d501ad0',
}[ENVIRONMENT];

export const NUTRIENT_KEYS = {
  macronutrients: {
    ENERGY_KILOCALORIES: 'energy-kilocalories',
    CARBOHYDRATES: 'total-carbohydrate',
    TOTAL_FAT: 'total-fat',
    PROTEIN: 'total-protein',
  },
  nutrients: {
    ADDED_SUGARS: 'added-sugars-by-total-sugars',
    SATURATED_FAT: 'total-saturated-fatty-acids-sfa',
    CHOLESTEROL: 'cholesterol',
    SODIUM: 'sodium',
    DIETARY_FIBER: 'total-dietary-fiber',
  },
  fats: {
    MONOUNSATURATED_FAT: 'total-monounsaturated-fatty-acids-mufa',
    POLYUNSATURATED_FAT: 'total-polyunsaturated-fatty-acids-pufa',
    OMEGA_3_FAT: 'omega-3-fatty-acids',
    TRANS_FAT: 'total-trans-fatty-acids-trans',
  },
  minerals: {
    CALCIUM: 'calcium',
    IRON: 'iron',
    POTASSIUM: 'potassium',
    MAGNESIUM: 'magnesium',
    PHOSPHORUS: 'phosphorus',
    ZINC: 'zinc',
    SELENIUM: 'selenium',
    COPPER: 'copper',
    MANGANESE: 'manganese',
  },
  vitamins: {
    VITAMIN_C: 'vitamin-c-ascorbic-acid',
    VITAMIN_A: 'total-vitamin-a-activity-retinol-activity-equivalents',
    VITAMIN_D: 'vitamin-d-calciferol',
    VITAMIN_E: 'vitamin-e-total-alpha-tocopherol',
    VITAMIN_K: 'vitamin-k-phylloquinone',
    FOLATE: 'total-folate',
    THIAMIN: 'thiamin-vitamin-b1',
    RIBOFLAVIN: 'riboflavin-vitamin-b2',
    NIACIN: 'niacin-vitamin-b3',
    VITAMIN_B6: 'vitamin-b6-pyridoxine-pyridoxyl-and-pyridoxamine',
    VITAMIN_B12: 'vitamin-b12-cobalamin',
    PANTOTHENIC_ACID: 'pantothenic-acid',
  },
};

export const NUTRIENT_KEYS_ALL = Object.values(NUTRIENT_KEYS).flatMap((group) => Object.values(group));

export const getKeysToFind = (type) => Object.values(NUTRIENT_KEYS[type] || {});
